import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Icon } from './icon'
import { Popover } from './popover'

export type SearchOption = { id: string; label: string; hint?: string }

// Case-insensitive substring match over label (and the optional hint suffix, so
// typing "USD" finds an account by its currency tag). JS toLowerCase is
// Unicode-aware, so this folds Cyrillic correctly — unlike D1's ASCII-only
// lower() on the server (see lib/sql.ts). Pure + exported for unit testing.
export function filterOptions(options: SearchOption[], query: string): SearchOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return options
  return options.filter(
    (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
  )
}

type Props = {
  options: SearchOption[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  /** Render a clear row ("—") at the top — for optional fields. */
  emptyOption?: boolean
  emptyLabel?: string
  disabled?: boolean
  /** className applied to the trigger (e.g. "input"). */
  className?: string
  /** inline style applied to the trigger (e.g. the form's inputStyle). */
  style?: React.CSSProperties
  /** Show the search box only once the list passes this length. */
  searchThreshold?: number
  /** How many rows to show before the user starts typing. */
  initialVisible?: number
  /**
   * Fixed trigger width for inline use (filter bars). Omit for form fields,
   * where the trigger fills its container.
   */
  width?: number
}

// A single-select combobox that looks like a native <select> but adds a search
// box once the list grows past `searchThreshold`. Drops into existing forms via
// `className`/`style`, so the trigger matches the surrounding inputs. The search
// box auto-hides for short lists, so it's safe to use for every reference
// dropdown regardless of size.
export function SearchSelect({
  options,
  value,
  onChange,
  placeholder = '—',
  emptyOption = false,
  emptyLabel = '—',
  disabled = false,
  className,
  style,
  searchThreshold = 10,
  initialVisible = 20,
  width,
}: Props) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [menuWidth, setMenuWidth] = useState<number>()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)

  const selected = options.find((o) => o.id === value)
  const showSearch = options.length > searchThreshold

  // The rendered rows: an optional "—" clear row, then the filtered options
  // (capped to `initialVisible` until the user types).
  const filtered = useMemo(() => filterOptions(options, q), [options, q])
  const truncated = !q.trim() && filtered.length > initialVisible
  const rows = useMemo<SearchOption[]>(() => {
    const visible = truncated ? filtered.slice(0, initialVisible) : filtered
    return emptyOption ? [{ id: '', label: emptyLabel }, ...visible] : visible
  }, [filtered, truncated, initialVisible, emptyOption, emptyLabel])

  // Reset transient state every time the menu opens, and remember the trigger
  // width so the popover lines up with the field.
  useLayoutEffect(() => {
    if (!open) return
    setMenuWidth(triggerRef.current?.offsetWidth)
    setQ('')
    setActive(0)
  }, [open])

  useEffect(() => {
    if (open && showSearch) searchRef.current?.focus()
  }, [open, showSearch])

  // Keep the highlighted row in view during arrow-key navigation.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const choose = (id: string) => {
    onChange(id)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, rows.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[active]
      if (row) choose(row.id)
    }
  }

  return (
    <span
      style={{
        position: 'relative',
        display: width != null ? 'inline-block' : 'block',
        width: width ?? '100%',
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={className}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          fontFamily: 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...style,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selected ? 'var(--fg-1)' : 'var(--fg-4)',
          }}
        >
          {selected ? labelWithHint(selected) : placeholder}
        </span>
        <Icon name="chevron-down" size={12} color="var(--fg-4)" />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} width={menuWidth ?? 280}>
        <div onKeyDown={onKeyDown}>
          {showSearch && (
            <div style={{ padding: 8, borderBottom: '1px solid var(--border-default)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 8px',
                  height: 30,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-3)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <Icon name="search" size={14} color="var(--fg-4)" />
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value)
                    setActive(0)
                  }}
                  placeholder="Пошук…"
                  style={{
                    flex: 1,
                    border: 0,
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--fg-1)',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    minWidth: 0,
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {rows.length === 0 ? (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--fg-3)' }}>Нічого не знайдено</div>
            ) : (
              rows.map((row, i) => (
                <button
                  key={row.id || '__empty'}
                  ref={i === active ? activeRef : undefined}
                  type="button"
                  onClick={() => choose(row.id)}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontSize: 12,
                    background: i === active ? 'var(--bg-3)' : 'transparent',
                    border: 0,
                    color: row.id ? 'var(--fg-1)' : 'var(--fg-3)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.id ? labelWithHint(row) : row.label}
                </button>
              ))
            )}
            {truncated && (
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--fg-3)' }}>
                ще {filtered.length - initialVisible} — почніть вводити для пошуку
              </div>
            )}
          </div>
        </div>
      </Popover>
    </span>
  )
}

function labelWithHint(o: SearchOption): string {
  return o.hint ? `${o.label} · ${o.hint}` : o.label
}
